import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import {
  CopyIcon,
  DownloadIcon,
  RefreshCwIcon,
  FilterIcon,
  BarChart3,
  CalendarDays,
  PackageSearch,
  Plus,
  AlertCircle,
  InfoIcon,
  Code2Icon,
} from 'lucide-react';
import { FailureLogModal } from '../../components/FailureLogModal';
import { useDrawer } from '../../hooks/useDrawer';
import OrderDetails from '../../components/OrderDetails';
import TrackingDetails from '../../components/TrackingDetails';
import toast from 'react-hot-toast';
import { Select } from '../../components/ui/Select';
import { truncateText } from '../../utils/truncate';
import DateRangeCalendar from '../../components/Calendar';
import { format } from 'date-fns';
import { FilterDrawer } from '../../components/FilterDrawer';
import { Checkbox } from '../../components/ui/Checkbox';
import { useOrders } from '../../hooks/useOrder';
import { useAuth } from '../../hooks/useAuth';
import { Loader } from '../../components/ui/Loader';
import { shipmentStatusTabs } from '../../constants/ShipmentStatus';
import { useModal } from '../../hooks/useModal';
import { ordersService, getShipmentIdsFromPickupResponse } from '../../services/orders';
import { EmptyScreen } from '../../components/ui/EmptyScreen';
import { useAddress } from '../../hooks/useAddress';
import { useCalculateRates } from '../../hooks/useRate';
import { RateSelection } from './add-order/rateSelection';
import { Edit16Filled } from '@fluentui/react-icons';
import { ViewToggle } from '../../components/ui/ViewToggle';
import { OrderCard } from '../../components/OrderCard';
import { zoneMeaningMap } from '../../constants/zoneMeaning';
import { useLocation, useNavigate } from 'react-router-dom';
import Tooltip from '../../components/ui/Tooltip';
import { useReport } from '../../hooks/useReport';
import { jsonToCSV, downloadCSV } from '../../utils/csv';
import { EditChannelOrderModal } from './EditChannelOrderModal';
import { ConfirmationModal } from './ConfirmationModal';
import { BulkProcessModal } from './bulk-orders/BulkProcessModal';
import { validateBulkOrders } from '../../utils/bulkUpload';
import { getShipmentStatusClasses, formatStatusDisplay } from '../../utils/status';
import { getPickupDisplayName } from '../../utils/format';


interface CancellationModalContentProps {
  orderNumber: string;
  onCancelShipment: () => void;
  onCancelOrder: () => void;
  onClose: () => void;
}

const CancellationModalContent: React.FC<CancellationModalContentProps> = ({
  orderNumber,
  onCancelShipment,
  onCancelOrder,
  onClose,
}) => {
  return (
    <div className="space-y-4 p-4 text-center">
      {/* Red Warning triangle icon */}
      <svg className="w-16 h-16 text-red-600 mx-auto" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3L2 21H22L12 3Z" fill="#EF4444" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 9V13" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 17H12.01" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      <h3 className="text-xl font-bold text-gray-905 mt-4 leading-tight text-gray-900">
        Do you want to cancel the Order or Shipment?
      </h3>
      <p className="text-sm text-gray-500 font-medium -mt-2">
        You can't undo this action.
      </p>

      {/* Informational warning block (yellow container) */}
      <div className="bg-amber-50/70 border border-amber-100 rounded-lg p-4 mt-6 text-left">
        <div className="flex gap-3 text-sm">
          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div className="space-y-3 leading-relaxed text-amber-900 font-medium">
            <p>
              Cancellation request will be forwarded to the courier partner. Once the cancellation is confirmed by the partner, the freight charges will be refunded and credited to your Parcelpicks wallet immediately.
            </p>
          </div>
        </div>
      </div>

      {/* Buttons at the bottom */}
      <div className="flex gap-4 pt-4">
        <button
          className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg px-6 py-3 font-semibold transition-colors shadow-sm text-sm"
          onClick={() => {
            onCancelOrder();
          }}
        >
          Cancel Order
        </button>
        <button
          className="flex-1 bg-white hover:bg-red-50 text-red-600 border border-red-500 rounded-lg px-6 py-3 font-semibold transition-colors text-sm"
          onClick={() => {
            onClose();
          }}
        >
          Close Popup
        </button>
      </div>
    </div>
  );
};

const CHANNEL_MAP: Record<string, { label: string; logo: string }> = {
  shopify: { label: 'Shopify', logo: '/shopify.svg' },
  // manual: { label: 'Manual', logo: '/manual.svg' },
  woocommerce: { label: 'WooCommerce', logo: '/icons/woocommerce.svg' },
  parcelpicks: { label: 'ParcelPicks', logo: 'Black.svg' },
  // amazon: { label: 'Amazon', logo: '/brandLogos/channel/amazon.png' },
  // magento: { label: 'Magento', logo: '/brandLogos/channel/magento.svg' },
  // wix: { label: 'Wix', logo: '/brandLogos/channel/wix.svg' },
};

const ChannelBadge = ({
  channelId,
  orderNumber,
  createdAt,
  order,
  onSelectOrder,
}: {
  channelId?: string;
  orderNumber: string;
  createdAt: string;
  order: any;
  onSelectOrder: (order: any) => void;
}) => {
  const channel =
    CHANNEL_MAP[channelId || 'parcelpicks'] || CHANNEL_MAP.parcelpicks;

  return (
    <div className="space-y-2 group text-center">
      {/* Channel Badge */}
      <div className="flex flex-row items-center justify-center gap-2 text-sm font-medium">
        <img
          src={channel.logo}
          alt={channel.label}
          className="xl:h-12 xl:w-12 h-8 w-8 lg:w-14 lg:h-14 md:w-14 md:h-14 object-contain"
        />
        <div className="flex flex-col gap-1 items-start justify-center">
          {/* Order Number */}
          <div className="flex items-center justify-center gap-2">
            <Tooltip text={orderNumber} side="top">
              <span
                onClick={() => onSelectOrder(order)}
                className="text-blue-600 font-semibold cursor-pointer hover:underline truncate max-w-[70px] inline-block align-middle"
              >
                {orderNumber}
              </span>
            </Tooltip>

            <CopyIcon
              className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 cursor-pointer"
              onClick={() => {
                navigator.clipboard.writeText(orderNumber);
                toast.success('Order ID copied');
              }}
            />
          </div>

          {/* Date */}
          <div className="text-xs text-gray-500 flex flex-col items-center">
            <span>{format(new Date(createdAt), 'dd MMM, yyyy')}</span>
            <span>{format(new Date(createdAt), 'hh:mm a')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const channelOptions = [
  { label: 'All Channels', value: '' },
  { label: 'Shopify', value: 'shopify' },
  { label: 'manual', value: 'manual' },
  { label: 'WooCommerce', value: 'woocommerce' },
];

export const bulkOptions = [
  { label: 'Bulk Actions', value: '' },
  { label: 'Bulk Label', value: 'label' },
  { label: 'Bulk Manifest', value: 'manifest' },
  { label: 'Bulk Cancel', value: 'cancel' },
];

export const zoneOptions = [
  { label: 'All Zones', value: '' },
  { label: 'Zone - 1', value: 'zone-1' },
  { label: 'Zone - 2', value: 'zone-2' },
  { label: 'Zone - 3', value: 'zone-3' },
  { label: 'Zone - 4', value: 'zone-4' },
  { label: 'Zone - 5', value: 'zone-5' },
  { label: 'Zone - 6', value: 'zone-6' },
];

export const paymentOptions = [
  { label: 'All Payments', value: '' },
  { label: 'COD', value: 'cod' },
  { label: 'Prepaid', value: 'online' },
];

export const courierOptions = [
  { label: 'All Couriers', value: '' },
  { label: 'Shadowfax', value: 'Shadowfax' },
  { label: 'Delhivery', value: 'Delhivery' },
  { label: 'Shree Maruti', value: 'Shree Maruti' },
];

export const shippingModeOptions = [
  { label: 'All Modes', value: '' },
  { label: 'By surface', value: 'By surface' },
  { label: 'By Air', value: 'By air' },
];

export const pageSizeOptions = [
  { label: '10 / page', value: '10' },
  { label: '20 / page', value: '20' },
  { label: '50 / page', value: '50' },
  { label: '100 / page', value: '100' },
];

const AllOrdersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { open, Drawer, close } = useDrawer();

  const handleTrackingClick = (order: any) => {
    open({
      element: (
        <TrackingDetails
          trackingId={order.awb_number || ''}
          rtoTrackingId={order.rto_awb_number || null}
          orderStatus={order.shipment_status || ''}
        />
      ),
      position: 'right',
      className: 'p-4 sm:p-6 md:p-10 w-full max-w-4xl',
      closable: true,
    });
  };
  const { open: openModal, close: closeModal, Modal: CustomModal } = useModal();
  const { user } = useAuth();
  const { mutate: calculateRates, isPending: isLoadingRates } = useCalculateRates();
  const [submittingOrderState, setSubmittingOrderState] = useState<{
    isSubmitting: boolean;
    submittingRateKey: string | null;
  }>({
    isSubmitting: false,
    submittingRateKey: null,
  });
  const { data: addressData } = useAddress(user?.id as string);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  useEffect(() => {
    if (location.state?.selectedOrder) {
      setSelectedOrder(location.state.selectedOrder);
      // Clear state to avoid re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const [view, setView] = useState('list');
  const [activeTab, setActiveTab] = useState<string>('Ready For Pickup');
  const { getShipmentReportAsync, isFetchingReport } = useReport(
    user?.id as string
  );
  const admin = user?.roleName === 'ADMIN';
  // Filter states
  const [channelFilter, setChannelFilter] = useState('');
  const [bulkAction, setBulkAction] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [zoneFilter, setZoneFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [courierFilter, setCourierFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [shippingModeFilter, setShippingModeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);
  const calendarWrapperRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isCalendarOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (!calendarWrapperRef.current) return;
      if (calendarWrapperRef.current.contains(e.target as Node)) return;
      setIsCalendarOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);

  const activeTabCode = useMemo(() => {
    const tab = shipmentStatusTabs.find((t) => t.statusText === activeTab);
    return tab?.statusCode || 'pending';
  }, [activeTab]);

  const {
    orders: apiOrders,
    totalCount,
    loading,
    error,
    refetch,
    handleCancelOrder,
    handleGetManifest,
  } = useOrders({
    userId: user?.id || '',
    storeId: (user?.storeId || '1') as string,
    // storeId: '34',
    shipmentStatus: activeTabCode === 'all' && statusFilter ? statusFilter : activeTabCode,
    page: currentPage,
    pageSize: rowsPerPage,
    searchTerm: searchTerm || undefined,
    isAdmin: admin,
    startDate,
    endDate,
    channelFilter,
    paymentFilter,
    courierFilter,
    shippingModeFilter,
    zoneFilter,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate]);

  const toggleRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (!paginatedOrders || paginatedOrders.length === 0) return;

    if (selectedIds.length === paginatedOrders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(
        paginatedOrders.map((order) => order.shipment_id || order.order_id)
      );
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    switch (key) {
      case 'channelFilter':
        setChannelFilter(value);
        break;
      case 'paymentFilter':
        setPaymentFilter(value);
        break;
      case 'courierFilter':
        setCourierFilter(value);
        break;
      case 'shippingModeFilter':
        setShippingModeFilter(value);
        break;
      case 'zoneFilter':
        setZoneFilter(value);
        break;
      case 'startDate':
        setStartDate(value);
        break;
      case 'endDate':
        setEndDate(value);
        break;
      case 'statusFilter':
        setStatusFilter(value);
        break;
      default:
        break;
    }
  };

  const openFilterDrawer = () => {
    open({
      Component: () => (
        <FilterDrawer
          noCalendar
          filters={{
            channelFilter,
            paymentFilter,
            courierFilter,
            shippingModeFilter,
            zoneFilter,
            statusFilter,
            startDate,
            endDate,
          }}
          options={{
            channelOptions: channelOptionsDynamic,
            paymentOptions: paymentOptionsDynamic,
            courierOptions: courierOptionsDynamic,
            shippingModeOptions: serviceOptionsDynamic,
            zoneOptions: zoneOptionsDynamic,
            statusOptions: isAllShipment ? statusOptionsDynamic : [],
          }}
          onFilterChange={handleFilterChange}
          onClose={() => close()}
        />
      ),
      closable: true,
      position: 'right',
      className: 'w-full max-w-md',
    });
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const paginatedOrders = apiOrders || [];

  const isPending = activeTabCode === 'pending';
  const isAllShipment = activeTabCode === 'all';

  const dynamicBulkOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];
    const isAdmin = admin;

    if (activeTabCode === 'all') {
      options.push({ label: 'Label', value: 'label' });
      options.push({ label: 'Manifest', value: 'manifest' });
      options.push({ label: 'Cancel', value: 'cancel' });
      options.push({ label: 'Clone Order', value: 'clone' });
      return options;
    }

    switch (activeTabCode) {
      case 'ready_for_pickup,out_for_pickup':
        options.push({ label: 'Label', value: 'label' });
        options.push({ label: 'Manifest', value: 'manifest' });
        options.push({ label: 'Cancel', value: 'cancel' });
        break;
      case 'in_transit':
      case 'rto_in_transit':
        if (isAdmin) {
          options.push({ label: 'Label', value: 'label' });
          options.push({ label: 'Manifest', value: 'manifest' });
          options.push({ label: 'Cancel', value: 'cancel' });
        }
        break;
      case 'delivered':
        options.push({ label: 'Clone Order', value: 'clone' });
        break;
      case 'pending':
        options.push({ label: 'Label', value: 'label' });
        options.push({ label: 'Cancel', value: 'cancel' });
        options.push({ label: 'Ship Now', value: 'ship' });
        options.push({ label: 'Clone Order', value: 'clone' });
        break;
      case 'failed':
        options.push({ label: 'Clone Order', value: 'clone' });
        break;
    }

    return options;
  }, [activeTabCode, admin]);

  const convertApiResponseToCourierRates = (apiResponse: any) => {
    if (!apiResponse?.couriers) return [];
    return apiResponse.couriers.map((courier: any) => ({
      courierId: courier.courier_id,
      courierName: courier.name,
      logoUrl: courier.logo_url,
      services: (courier.service_types || []).map((service: any) => {
        const rb = service.rate_breakup || {};
        return {
          serviceId: service.service_id,
          serviceName: service.service_name,
          displayName: service.display_name,
          tat: service.expected_delivery_days,
          volumetricWeight: service.volumetric_weight || 0,
          zone: service.zone,
          baseWeightSlab: service.rate_breakup?.base_weight_slab as number,
          rtoCharge: service.rate_breakup?.courier_total_charge ?? 0,
          weight: apiResponse.display_weight || 0,
          totalCharge: rb.total_charge || 0,
          baseRate: rb.base_rate || 0,
          gst: rb.goods_services_tax || 0,
          fuel: rb.fuel_surcharge || 0,
        };
      }),
    }));
  };

  const handleCloneOrder = (order: any) => {
    const prefillData = {
      deliveryDetails: {
        companyName:
          order.destination_address?.company_name ||
          order.destination_address?.contact_name ||
          '',
        contactName: order.destination_address?.contact_name || '',
        address1: order.destination_address?.address1 || '',
        address2: order.destination_address?.address2 || '',
        landmark: order.destination_address?.landmark || '',
        pinCode: order.destination_address?.pincode || '',
        city: order.destination_address?.city || '',
        state: order.destination_address?.state || '',
        phoneNumber: order.destination_address?.contact_no || '',
        email: order.destination_address?.email || '',
        altContactNo: order.destination_address?.alt_contact_no || '',
      },
      orderDetails: {
        orderId: order.order_number ? String(order.order_number) : '',
        weight: String(order.dead_weight || ''),
        dimensions: {
          length: String(order.length || ''),
          breadth: String(order.breadth || ''),
          height: String(order.height || ''),
        },
      },
      products: order.products && order.products.length > 0
        ? order.products.map((p: any) => ({
          productName: p.productName || '',
          quantity: p.quantity || 1,
          invoiceValue: String(p.invoiceValue || ''),
          sku: p.sku || '',
          hsn: p.hsn || '',
        }))
        : [
          {
            productName: order.package_desc || 'Product',
            quantity: order.quantity || 1,
            invoiceValue: String(order.shipment_value || 0),
            sku: order.sku || '',
            hsn: order.hsn || '',
          },
        ],
      warehouse: String(
        order.pickup_id || order.pickup_address_id || order.origin_address?.pickup_id || order.origin_address?.id || ''
      ),
      shipmentType: order.payment_mode?.toLowerCase() === 'cod' ? 'cod' : 'prepaid',
    };
    navigate('/orders/add', { state: prefillData });
  };

  const handleShipNow = (order: any) => {
    if (!order.origin_address || !order.origin_address.pickup_id) {
      toast.error('Pickup warehouse is required. Please Edit the order to select a pickup address first.');
      return;
    }

    const toastId = toast.loading('Calculating rates...');

    const length = order.length || 1;
    const width = order.breadth || 1;
    const height = order.height || 1;

    const rateData = {
      pickup_pincode: String(order.origin_address.pincode),
      delivery_pincode: order.destination_address.pincode,
      invoice_value: order.shipment_value || 0,
      payment_mode: order.payment_mode?.toLowerCase() === 'cod' ? 'COD' : 'online',
      product_type: 'Parcel',
      delivery_mode: 'Both',
      insurance: false,
      number_of_package: 1,
      package_details: [
        {
          package_content: order.package_desc || 'Product',
          no_of_items: order.quantity || 1,
          invoice_value: order.shipment_value || 0,
          package_dimension: {
            weight: order.dead_weight || 0.5,
            length: length,
            width: width,
            height: height,
          },
        },
      ],
    };

    calculateRates(
      {
        userId: user?.id as string,
        rateData: rateData as any,
      },
      {
        onSuccess: (apiResponseData) => {
          toast.dismiss(toastId);
          const formattedRates = convertApiResponseToCourierRates(apiResponseData);

          const handleRateConfirm = async (selectedRate: any) => {
            const volumetricWeight = (length * width * height) / 5000;
            const deadWeight = order.dead_weight || 0.5;
            const chargeableWeight = Math.max(deadWeight, volumetricWeight);

            const sanitizePhone = (phone: string) => {
              if (!phone) return '';
              const digits = phone.replace(/\D/g, '');
              return digits.length > 10 ? digits.slice(-10) : digits;
            };

            const payload = {
              user_id: Number(user?.id) || 1472,
              courier_id: selectedRate.courierId,
              service_id: selectedRate.serviceId,
              plan_id: 1,
              service_type: selectedRate.serviceName,
              order_number: order.order_number,
              payment_mode: order.payment_mode?.toLowerCase() === 'cod' ? 'COD' : 'online',
              invoice_value: order.shipment_value || 0,
              pickup_pincode: String(order.origin_address.pincode),
              delivery_pincode: order.destination_address.pincode,
              number_of_package: 1,
              insurance: false,
              confirm_order: true,
              payment_status: order.payment_mode?.toLowerCase() !== 'cod',
              pickup_address_id: order.origin_address.pickup_id,
              userName: user?.firstName ?? (order.destination_address.contact_name || 'User'),
              userEmail: user?.email ?? (order.destination_address.email || ''),
              pickup_address: {
                contact_name: order.origin_address.contact_name || '',
                company_name: order.origin_address.company_name || '',
                address1: order.origin_address.address1 || '',
                address2: order.origin_address.address2 ?? undefined,
                landmark: order.origin_address.landmark || 'N/A',
                city: order.origin_address.city || '',
                pincode: String(order.origin_address.pincode) || '',
                state: order.origin_address.state || '',
                country: 'IN',
                contact_no: sanitizePhone(order.origin_address.contact_no || ''),
                alt_contact_no: order.origin_address.alt_contact_no ? sanitizePhone(order.origin_address.alt_contact_no) : undefined,
                email: order.origin_address.email || '',
              },
              delivery_address: {
                contact_name: order.destination_address.contact_name || '',
                company_name: order.destination_address.company_name || '',
                address1: order.destination_address.address1 || '',
                address2: order.destination_address.address2 ?? undefined,
                landmark: order.destination_address.landmark || 'N/A',
                city: order.destination_address.city || '',
                pincode: order.destination_address.pincode || '',
                state: order.destination_address.state || '',
                country: 'IN',
                contact_no: sanitizePhone(order.destination_address.contact_no || ''),
                alt_contact_no: order.destination_address.alt_contact_no ? sanitizePhone(order.destination_address.alt_contact_no) : undefined,
                email: order.destination_address.email || '',
              },
              package_detail: [
                {
                  no_of_items: order.quantity || 1,
                  package_content: order.package_desc || 'Product',
                  invoice_value: order.shipment_value || 0,
                  package_dimension: {
                    weight: deadWeight,
                    length,
                    width: width,
                    height,
                  },
                  volumetric_weight: volumetricWeight,
                  chargeable_weight: chargeableWeight,
                },
              ],
            };

            const rateKey = `${selectedRate.courierId}-${selectedRate.serviceId}`;
            setSubmittingOrderState({ isSubmitting: true, submittingRateKey: rateKey });
            const createToastId = toast.loading('Creating shipment...');

            try {
              const pickupRes = await ordersService.createPickup(user?.id as string, payload as any);
              const shipmentIds = getShipmentIdsFromPickupResponse(pickupRes);

              if (!shipmentIds.length) {
                toast.error('No shipment IDs returned. Please try again.', { id: createToastId });
                return;
              }

              toast.loading('Confirming order...', { id: createToastId });
              await ordersService.createOrders({ shipmentIds });
              toast.success('Order created successfully.', { id: createToastId });

              if (order.order_id) {
                try {
                  const trackingNumber = (pickupRes.shipmentPackages as any[])?.[0]?.tracking_number;
                  if (trackingNumber) {
                    await ordersService.updateChannelAWB({
                      user_id: Number(user?.id),
                      sOrder_id: order.order_id,
                      awb: trackingNumber,
                    });
                  }
                } catch (awbErr) {
                  console.error('Failed to update channel AWB:', awbErr);
                }
              }

              setTimeout(() => {
                closeModal();
                window.location.reload();
              }, 1500);
            } catch (err: any) {
              const message = err?.response?.data?.message ?? err?.message ?? 'Failed to create order.';
              toast.error(message, { id: createToastId });
            } finally {
              setSubmittingOrderState({ isSubmitting: false, submittingRateKey: null });
            }
          };

          openModal({
            Component: () => (
              <RateSelection
                rates={formattedRates}
                isLoading={false}
                isError={false}
                onClose={closeModal}
                isSubmittingOrder={submittingOrderState.isSubmitting}
                submittingRateKey={submittingOrderState.submittingRateKey}
                onBack={closeModal}
                onConfirm={handleRateConfirm}
                orderSummary={{
                  pickupState: order.origin_address.state || '',
                  pickupCity: order.origin_address.city || '',
                  pickupPincode: String(order.origin_address.pincode || ''),
                  deliveryState: order.destination_address.state || '',
                  deliveryCity: order.destination_address.city || '',
                  deliveryPincode: order.destination_address.pincode || '',
                  invoiceValue: order.shipment_value || 0,
                  paymentMode: order.payment_mode || 'online',
                  productDetails: order.package_desc || 'Product',
                  quantity: order.quantity || 1,
                  weight: order.dead_weight || 0.5,
                  length,
                  breadth: width,
                  height,
                  orderId: order.order_number,
                }}
              />
            ),
            centered: true,
            closable: true,
            className: 'w-full',
          });
        },
        onError: (err) => {
          toast.dismiss(toastId);
          toast.error(err.message || 'Failed to calculate rates.');
        },
      }
    );
  };

  const gridColsClass = useMemo(() => {
    let cols = '';
    if (admin) {
      if (isPending)
        cols = '[grid-template-columns:40px_80px_1fr_1fr_1fr_100px_120px_80px]';
      if (!isPending)
        cols =
          '[grid-template-columns:40px_80px_1fr_1fr_1.2fr_1fr_100px_160px_80px]';
      if (isAllShipment)
        cols =
          '[grid-template-columns:40px_80px_1fr_1fr_1.2fr_1fr_100px_160px_80px_100px]';
    } else {
      if (isPending)
        cols = '[grid-template-columns:40px_1fr_1fr_1fr_100px_120px_80px]';
      if (!isPending)
        cols =
          '[grid-template-columns:40px_1fr_1fr_1.2fr_1fr_100px_120px_80px]';
      if (isAllShipment)
        cols =
          '[grid-template-columns:40px_1fr_1fr_1.2fr_1fr_100px_120px_80px_100px]';
    }

    return `grid ${cols} gap-2`;
  }, [admin, isPending, isAllShipment]);

  const handleBulkManifest = async (selectedOrders: any[]) => {
    const groups: Record<string, string[]> = {};
    selectedOrders.forEach((o) => {
      if (o.courier_id) {
        if (!groups[o.courier_id]) groups[o.courier_id] = [];
        if (o.shipment_id) groups[o.courier_id].push(o.shipment_id);
      }
    });

    const shipments = Object.entries(groups).map(
      ([courierId, shipmentIds]) => ({
        courierId,
        shipmentIds: shipmentIds.join(','),
      })
    );

    if (shipments.length === 0) {
      toast.error('No valid shipment IDs found for selected orders');
      return;
    }

    const payload = { shipmentData: { shipments } };
    try {
      const res = await handleGetManifest(payload);
      if (res.success) {
        toast.success('Manifests generated');
        let url = '';
        if (typeof res.data?.data === 'string') url = res.data.data;
        else if (typeof res.data === 'string') url = res.data;
        else if (res.data?.url) url = res.data.url;

        if (url) {
          const a = document.createElement('a');
          a.href = url;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to generate manifests');
    }
  };

  const handleIndividualLabel = async (order: any) => {
    if (!order.shipment_id || !user?.id) {
      if (order.label_url) {
        window.open(order.label_url, '_blank');
        return;
      }
      toast.error('No shipment ID available for this order');
      return;
    }

    setIsGeneratingLabel(true);
    const toastId = toast.loading('Generating label...');
    try {
      const res = await ordersService.getZipLabels(user.id, {
        standard: [Number(order.shipment_id)],
        thermal: [],
      });
      const url =
        res.std_pdf ||
        res.ther_pdf ||
        res.url ||
        res.data ||
        res.data?.url ||
        (res.success && res.data);

      if (url && res.code !== 404) {
        window.open(url, '_blank');
        toast.success('Label generated successfully', { id: toastId });
      } else if (order.label_url) {
        window.open(order.label_url, '_blank');
        toast.success('Label opened', { id: toastId });
      } else {
        throw new Error(res.message || 'Failed to generate label');
      }
    } catch (err: any) {
      if (order.label_url) {
        window.open(order.label_url, '_blank');
      } else {
        toast.error(err.message || 'Failed to generate label', { id: toastId });
      }
    } finally {
      setIsGeneratingLabel(false);
    }
  };

  const isPendingOrReady = (status: string) => {
    if (!status) return false;
    const lower = status.toLowerCase();
    return (
      lower === 'pending' ||
      lower === 'failed' ||
      lower.includes('ready') ||
      lower.includes('pickup') ||
      lower.includes('manifest') ||
      lower.includes('label') ||
      lower.includes('exception')
    );
  };

  const isEligibleForManifestOrLabel = (status: string) => {
    if (admin) return true;
    return isPendingOrReady(status);
  };

  const isEligibleForCancel = (status: string) => {
    // Cancel is strictly only for pending or ready for pickup regardless of admin
    return isPendingOrReady(status);
  };

  // Handle bulk actions
  useEffect(() => {
    if (!bulkAction || selectedIds.length === 0) return;

    if (bulkAction === 'label') {
      const selectedWithShipmentId =
        apiOrders?.filter(
          (o) =>
            selectedIds.includes(o.shipment_id || o.order_id) && o.shipment_id
        ) || [];

      if (selectedWithShipmentId.length === 0) {
        toast.error('No selected orders have a valid shipment ID');
      } else {
        const shipmentIds = selectedWithShipmentId.map((o) =>
          Number(o.shipment_id)
        );
        const toastId = toast.loading(
          `Generating labels for ${shipmentIds.length} orders...`
        );
        ordersService
          .getZipLabels(user?.id as string, {
            standard: shipmentIds,
            thermal: [],
          })
          .then((res: any) => {
            // Updated to check for std_pdf as per user feedback
            const url =
              res.std_pdf ||
              res.ther_pdf ||
              res.url ||
              res.data ||
              res.data?.url ||
              (res.success && res.data);

            if (url && res.code !== 404) {
              window.open(url, '_blank');
              toast.success('Labels generated successfully', { id: toastId });
            } else {
              toast.error(res.message || 'Failed to generate labels', {
                id: toastId,
              });
            }
          })
          .catch((err: any) => {
            toast.error(err.message || 'Failed to generate labels', {
              id: toastId,
            });
          });
      }
    }
    if (bulkAction === 'manifest') {
      const selectedWithManifest =
        apiOrders?.filter(
          (o) =>
            selectedIds.includes(o.shipment_id || o.order_id) &&
            isEligibleForManifestOrLabel(o.shipment_status || '')
        ) || [];

      if (selectedWithManifest.length === 0) {
        if (!admin)
          toast.error(
            'Manifest can only be generated for Pending or Ready for Pickup orders'
          );
        else toast.error('No valid orders selected for manifest');
      } else {
        handleBulkManifest(selectedWithManifest).catch(() => { });
      }
    }
    if (bulkAction === 'cancel') {
      const selectedOrders =
        apiOrders?.filter(
          (o) =>
            selectedIds.includes(o.shipment_id || o.order_id) &&
            isEligibleForCancel(o.shipment_status || '')
        ) || [];

      if (selectedOrders.length === 0) {
        toast.error('Only Pending or Ready for Pickup orders can be cancelled');
      } else {
        const triggerBulkCancel = () => {
          closeModal();
          const cancelPromises = selectedOrders.map((o) =>
            o.shipment_id ? handleCancelOrder(o.shipment_id) : Promise.resolve()
          );
          toast.promise(Promise.all(cancelPromises), {
            loading: `Cancelling ${selectedOrders.length} orders...`,
            success: 'Orders cancelled successfully',
            error: (err: any) =>
              typeof err === 'string'
                ? err
                : err?.message || 'Failed to cancel some orders',
          });
        };

        openModal({
          element: (
            <CancellationModalContent
              orderNumber={`${selectedOrders.length} selected orders`}
              onCancelOrder={triggerBulkCancel}
              onCancelShipment={triggerBulkCancel}
              onClose={closeModal}
            />
          ),
          centered: true,
          closable: true,
          className: 'max-w-lg',
        });
      }
    }

    if (bulkAction === 'clone') {
      const selectedOrders =
        apiOrders?.filter((o) =>
          selectedIds.includes(o.shipment_id || o.order_id)
        ) || [];

      if (selectedOrders.length === 1) {
        const order = selectedOrders[0];
        const prefillData = {
          deliveryDetails: {
            companyName: order.destination_address?.contact_name || '',
            address1: order.destination_address?.address1 || '',
            address2: order.destination_address?.address2 || '',
            landmark: order.destination_address?.landmark || '',
            pinCode: order.destination_address?.pincode || '',
            city: order.destination_address?.city || '',
            state: order.destination_address?.state || '',
            phoneNumber: order.destination_address?.contact_no || '',
            email: order.destination_address?.email || '',
          },
          orderDetails: {
            orderId: order.order_number,
            weight: String(order.dead_weight || ''),
            dimensions: {
              length: String(order.length || ''),
              breadth: String(order.breadth || ''),
              height: String(order.height || ''),
            },
          },
          products: [
            {
              productName: order.package_desc || 'Product',
              quantity: order.quantity || 1,
              invoiceValue: order.shipment_value || 0,
              sku: '',
              hsn: '',
            },
          ],
          warehouse: order.pickup_id,
        };
        navigate('/orders/add', { state: prefillData });
      } else if (selectedOrders.length > 1) {
        // Bulk Clone Direct Modal
        const mappedOrders = selectedOrders.map((o: any) => ({
          orderId: `${o.order_number}`,
          receiverName: o.destination_address?.contact_name,
          receiverPhone: o.destination_address?.contact_no,
          receiverEmail: o.destination_address?.email,
          address: o.destination_address?.address1,
          address2: o.destination_address?.address2,
          landmark: o.destination_address?.landmark,
          pincode: o.destination_address?.pincode,
          city: o.destination_address?.city,
          state: o.destination_address?.state,
          weight: Number(o.dead_weight),
          length: Number(o.length),
          width: Number(o.breadth),
          height: Number(o.height),
          productName: o.package_desc,
          quantity: Number(o.quantity),
          invoiceValue: Number(o.shipment_value),
          paymentMode:
            o.payment_mode?.toUpperCase() === 'COD' ? 'COD' : 'Prepaid',
          pickupDate: new Date().toISOString().split('T')[0],
          sku: o.sku,
          hsn: o.hsn,
          altPhone: o.destination_address?.alt_contact_no,
          origin_address: o.origin_address,
          pickup_id: o.pickup_id,
        }));

        const validated = validateBulkOrders(mappedOrders as any);

        openModal({
          element: (
            <BulkProcessModal
              orders={validated}
              warehouse={null}
              onClose={() => {
                closeModal();
                setSelectedIds([]);
                setBulkAction('');
                refetch();
              }}
            />
          ),
          className: 'p-0 max-w-none w-screen h-screen',
          closable: true,
        });
      }
    }

    setBulkAction('');
  }, [
    bulkAction,
    selectedIds,
    apiOrders,
    handleCancelOrder,
    handleGetManifest,
    addressData,
    openModal,
    closeModal,
    user?.id,
    admin,
  ]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [activeTab]);

  // Handle auto-unselect when rowsPerPage changes or data changes
  // As requested: if selected orders are 100 then reduce page size to 20,
  // then only 20 should be select rest will get auto unselected.
  useEffect(() => {
    if (selectedIds.length > 0 && apiOrders?.length > 0) {
      const currentShipmentIds = apiOrders.map(
        (o) => o.shipment_id || o.order_id
      );
      setSelectedIds((prev) =>
        prev.filter((id) => currentShipmentIds.includes(id))
      );
    }
  }, [rowsPerPage, apiOrders]);

  const channelOptionsDynamic = useMemo(() => {
    return [
      { label: 'All Channels', value: '' },
      // { label: 'Shopify', value: 'shopify' },
      // { label: 'Manual', value: 'manual' },
      { label: 'WooCommerce', value: 'woocommerce' },
      // { label: 'Amazon', value: 'amazon' },
      // { label: 'Indiamart', value: 'indiamart' },
      // { label: 'Magento', value: 'magento' },
      // { label: 'Wix', value: 'wix' },
      { label: 'ParcelPicks', value: 'parcelpicks' }
    ];
  }, []);

  const paymentOptionsDynamic = useMemo(() => {
    return [
      { label: 'All Payments', value: '' },
      { label: 'COD', value: 'cod' },
      { label: 'online', value: 'online' },
    ];
  }, []);

  const courierOptionsDynamic = useMemo(() => {
    return [
      { label: 'All Couriers', value: '' },
      { label: 'Delhivery', value: 'Delhivery' },
      { label: 'XpressBees', value: 'XpressBees' },
      { label: 'Shree Maruti', value: 'Shree Maruti' },
      // { label: 'Shadowfax', value: 'Shadowfax' },
      // { label: 'DTDC', value: 'DTDC' },
      // { label: 'BlueDart', value: 'BlueDart' },
      { label: 'Ekart', value: 'Ekart' },
      { label: 'Amazon', value: 'Amazon' },
      // { label: 'Ecom Express', value: 'Ecom Express' },
      // { label: 'KerryIndevExpress', value: 'KerryIndevExpress' }
    ];
  }, []);

  const normalizeServiceName = (name?: string) => {
    if (!name) return null;

    // Remove anything inside brackets like (PE)
    return name
      .replace(/\s*\(.*?\)\s*/g, '')
      .trim()
      .toLowerCase();
  };

  const serviceOptionsDynamic = useMemo(() => {
    return [
      { label: 'All Modes', value: '' },
      { label: 'By surface', value: 'By surface' },
      { label: 'By Air', value: 'By air' },
    ];
  }, []);

  const normalizeZoneKey = (val: string) => {
    return val.replace(/[-\s]/g, ' ').toUpperCase().trim();
  };

  const zoneOptionsDynamic = useMemo(() => {
    return [
      { label: 'All Zones', value: '' },
      { label: 'Zone A - Within City', value: 'zone-1' },
      { label: 'Zone B - Within State', value: 'zone-2' },
      { label: 'Zone C - Metro to Metro', value: 'zone-3' },
      { label: 'Zone D - Rest of India', value: 'zone-4' },
      { label: 'Zone E - Special Location', value: 'zone-5' },
    ];
  }, []);

  const statusOptionsDynamic = useMemo(() => {
    return [
      { label: 'All Statuses', value: 'all' },
      { label: 'In Transit', value: 'in_transit' },
      { label: 'Delivered', value: 'delivered' },
      { label: 'RTO Intransit', value: 'rto_intransit' },
      { label: 'Ready For Pickup', value: 'ready_for_pickup' },
      { label: 'Cancelled', value: 'cancelled' },
    ];
  }, []);

  if (selectedOrder) {
    return (
      <OrderDetails
        order={{
          // IDs
          order_id: selectedOrder.order_id,
          order_number: selectedOrder.order_number,
          shipment_id: selectedOrder.shipment_id,
          shopify_order_id: selectedOrder.shopify_order_id ?? null,

          // Dates
          created_at: selectedOrder.created_at,
          shipment_status_date: selectedOrder.shipment_status_date ?? null,

          // Status & Courier
          shipment_status: selectedOrder.shipment_status ?? null,
          courier_name: selectedOrder.courier_name ?? null,
          courier_id: selectedOrder.courier_id ?? null,
          service_name: selectedOrder.service_name ?? null,
          service_id: selectedOrder.service_id ?? null,
          awb_number: selectedOrder.awb_number ?? null,
          label_url: selectedOrder.label_url ?? null,
          pickup_id: selectedOrder.pickup_id ?? null,
          shipment_message_detail:
            selectedOrder.shipment_message_detail ?? null,
          shipment_type: selectedOrder.shipment_type ?? null,

          // Financials
          shipment_value: selectedOrder.shipment_value ?? null,
          payment_mode: selectedOrder.payment_mode ?? null,
          cod_amount: selectedOrder.cod_amount ?? null,
          shipping_cost: selectedOrder.shipping_cost ?? null,

          // Package
          dead_weight: selectedOrder.dead_weight ?? null,
          weight_unit: selectedOrder.weight_unit ?? 'Kg',
          volumetric_weight: selectedOrder.volumetric_weight ?? null,
          chargeable_weight: selectedOrder.chargeable_weight ?? null,
          base_weight_slab: selectedOrder.base_weight_slab ?? null,
          length: selectedOrder.length ?? null,
          breadth: selectedOrder.breadth ?? null,
          height: selectedOrder.height ?? null,
          dimension_unit: selectedOrder.dimension_unit ?? 'cm',
          package_desc: selectedOrder.package_desc ?? null,
          quantity: selectedOrder.quantity ?? null,

          // Routing
          zone: selectedOrder.zone ?? null,
          tat: selectedOrder.tat ?? null,
          shipping_mode: selectedOrder.shipping_mode ?? null,
          channel_id: selectedOrder.channel_id ?? null,

          // Addresses
          origin_address: selectedOrder.origin_address ?? null,
          destination_address: selectedOrder.destination_address ?? null,
          return_address: selectedOrder.return_address ?? null,

          // Store & Tags
          store_id: selectedOrder.store_id ?? null,
          shop_name: selectedOrder.shop_name ?? null,
          customer_note: selectedOrder.customer_note ?? null,
          tags: selectedOrder.tags ?? null,

          // Details
          rate_details: selectedOrder.rate_details ?? null,
          package_detail: selectedOrder.package_detail ?? null,
          extra_weight: selectedOrder.extra_weight ?? null,
          courier_charged_weight: selectedOrder.courier_charged_weight ?? null,
          overcharged_amount: selectedOrder.overcharged_amount ?? null,
          courier_rate_details: selectedOrder.courier_rate_details ?? null,
        }}
        onBack={() => setSelectedOrder(null)}
      />
    );
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd MMM, yyyy • hh:mm a');
  };

  const ReportModalContent = () => {
    const [selStart, setSelStart] = useState<Date | null>(new Date());
    const [selEnd, setSelEnd] = useState<Date | null>(new Date());

    const onDownload = async () => {
      try {
        const res: any = await getShipmentReportAsync({
          startDate: selStart ? format(selStart, 'yyyy-MM-dd') : undefined,
          endDate: selEnd ? format(selEnd, 'yyyy-MM-dd') : undefined,
        });

        if (!res || !res.success) {
          toast.error(res?.message || 'No report data found');
          return;
        }

        // Handle cases where report is sent to email instead of direct download
        if (res.showPopUp || (!res.file && !res.url && !res.data?.url && !Array.isArray(res.data))) {
          toast.success(res.message || 'The requested data has been sent successfully to your registered Email ID');
          closeModal();
          return;
        }

        // Handle the base64 encoded 'file' property from the API response
        if (res.file) {
          try {
            // Robustly decode base64 to handle UTF-8 characters
            const binaryString = atob(res.file);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const decoder = new TextDecoder('utf-8');
            const csv = decoder.decode(bytes);

            downloadCSV(
              csv,
              `shipment_report_${format(new Date(), 'yyyy-MM-dd')}.csv`
            );
          } catch (e) {
            // Fallback to direct string if decoding fails
            downloadCSV(
              res.file,
              `shipment_report_${format(new Date(), 'yyyy-MM-dd')}.csv`
            );
          }
        } else if (res.url) {
          window.open(res.url, '_blank');
        } else if (res.data?.url) {
          window.open(res.data.url, '_blank');
        } else if (Array.isArray(res.data)) {
          const csv = jsonToCSV(res.data);
          downloadCSV(
            csv,
            `shipment_report_${format(new Date(), 'yyyy-MM-dd')}.csv`
          );
        } else {
          toast.error('Failed to process report data');
          return;
        }

        toast.success(res.message || 'Report downloaded successfully');
        closeModal();
      } catch (err) {
        console.error('Report download error:', err);
        toast.error('Failed to download report');
      }
    };

    return (
      <div className="p-4 space-y-6">
        <div className="flex flex-col items-center">
          <CalendarDays className="h-12 w-12 text-teal mb-2" />
          <h2 className="text-xl font-bold">Download Shipment Report</h2>
          <p className="text-sm text-gray-500">
            Select a date range for your report
          </p>
        </div>

        <div className="flex justify-center border rounded-xl p-4 bg-gray-50">
          <DateRangeCalendar
            startDate={selStart}
            endDate={selEnd}
            onChange={(s, e) => {
              setSelStart(s);
              setSelEnd(e);
            }}
          />
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-teal hover:bg-teal/90 text-white"
            onClick={onDownload}
            disabled={isFetchingReport}
          >
            {isFetchingReport ? (
              <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <DownloadIcon className="h-4 w-4 mr-2" />
            )}
            Download CSV
          </Button>
        </div>
      </div>
    );
  };

  const handleDownloadReport = () => {
    openModal({
      element: <ReportModalContent />,
      className: 'max-w-xl',
      closable: true,
    });
  };

  return (
    <div className="px-4 xl:px-0 py-4 xl:py-6" ref={topRef}>
      {/* Header */}
      <div className="flex flex-col gap-2 xl:flex-row xl:justify-between xl:items-center">
        <div className="flex flex-col xl:flex-row gap-2 items-center">
          <div className="mb-6">
            <h1 className="text-2xl xl:text-3xl font-bold text-text mb-1">
              All Orders
            </h1>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>
                View and track all your scheduled and unscheduled shipments.
              </span>
            </div>
            <div className="ml-4"></div>
          </div>
        </div>
        {/* Sync Button */}
        <Button
          variant="outline"
          className="text-base hidden lg:flex gap-1"
          onClick={() => refetch()}
          disabled={loading}
        >
          <p className="hidden lg:block">Sync</p>
          <RefreshCwIcon
            className={`h-4 w-4 ml-2 ${loading ? 'animate-spin' : ''}`}
          />
        </Button>
      </div>

      <div className="flex flex-wrap items-start gap-4 mb-0">
        {/* Search + Actions */}
        <div>
          {/* Search */}
          <div className="w-48 xl:w-96">
            <Input
              placeholder={
                admin
                  ? 'Search by Company Name or Tracking Number or CRN'
                  : 'Search by Shipment ID'
              }
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                if (
                  val.trim().length > 0 &&
                  activeTab !== 'All Shipments' &&
                  activeTab !== 'Channel Order' &&
                  activeTab !== 'Failed'
                ) {
                  setActiveTab('All Shipments');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  refetch();
                }
              }}
            />
          </div>
        </div>

        <div ref={calendarWrapperRef} className="relative">
          <button
            type="button"
            aria-label="Select date range"
            onClick={() => setIsCalendarOpen((prev) => !prev)}
            className="p-2 rounded-lg border bg-white hover:bg-gray-50 text-teal"
          >
            <CalendarDays className="h-5 w-5" />
          </button>

          {isCalendarOpen && (
            <div className="absolute z-50  top-full mt-2">
              <DateRangeCalendar
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                  if (end) setIsCalendarOpen(false);
                }}
              />
            </div>
          )}
        </div>

        <Button
          variant="outline"
          onClick={openFilterDrawer}
          className="text-teal hover:text-white font-semibold text-base"
        >
          <FilterIcon className="h-4 w-4 mr-2" />
          <p className="hidden lg:block">More Filters</p>
        </Button>
        {/* Sync Button */}
        <Button
          variant="outline"
          className="text-base block lg:hidden"
          onClick={() => refetch()}
          disabled={loading}
        >
          <p className="hidden lg:block">Sync</p>
          <RefreshCwIcon
            className={`h-4 w-4 ml-2 ${loading ? 'animate-spin' : ''}`}
          />
        </Button>
        {/* Grid-view Select All — only in grid view */}
        {view === 'grid' && (
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border bg-white hover:bg-teal/5 text-teal text-sm font-medium transition-colors"
          >
            <Checkbox
              checked={
                selectedIds.length === paginatedOrders.length &&
                paginatedOrders.length > 0
              }
              onChange={toggleSelectAll}
            />
            <span>Select All</span>
          </button>
        )}

        {/* Bulk Action */}
        {selectedIds.length > 0 && (
          <div className="">
            <Select
              placeholder="Bulk Action"
              options={dynamicBulkOptions}
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
            />
          </div>
        )}
        {/* Reset Button */}
        {(channelFilter ||
          paymentFilter ||
          courierFilter ||
          shippingModeFilter ||
          zoneFilter ||
          startDate ||
          endDate ||
          searchTerm) && (
            <Button
              variant="outline"
              onClick={() => {
                setChannelFilter('');
                setPaymentFilter('');
                setCourierFilter('');
                setShippingModeFilter('');
                setSelectedIds([]);
                setZoneFilter('');
                setStatusFilter('');
                setStartDate(null);
                setEndDate(null);
                setSearchTerm('');
                toast.success('Filters cleared');
              }}
              className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
            >
              Reset Filters
            </Button>
          )}
      </div>

      {/* Tabs */}
      <div className="mt-6 xl:mt-0 flex items-center justify-between border-b mb-3">
        <div className="flex gap-4 xl:gap-12 text-base xl:text-lg no-scrollbar font-semibold overflow-x-auto">
          {shipmentStatusTabs.map((tab) => (
            <button
              key={tab.statusId}
              onClick={() => setActiveTab(tab.statusText)}
              className={`pb-2 whitespace-nowrap ${activeTab === tab.statusText
                ? 'border-b-2 border-teal text-text font-medium'
                : 'text-gray-800'
                }`}
            >
              {tab.statusText}
            </button>
          ))}
        </div>
        <div className="hidden xl:flex items-centers mb-2 gap-4">
          <Button
            variant="outline"
            className="text-teal font-semibold text-sm "
            onClick={handleDownloadReport}
            disabled={isFetchingReport}
          >
            {isFetchingReport ? (
              <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <BarChart3 className="h-4 w-4 mr-2" />
            )}
            Shipment Report
          </Button>
          <ViewToggle view={view} setView={setView} />
        </div>
      </div>
      {/* Selected Count Banner — between filters and status tabs */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3  mb-2 px-1">
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-teal/10 text-teal text-sm font-semibold rounded-full border border-teal/20">
            <span className="w-2 h-2 rounded-full bg-teal inline-block" />
            {selectedIds.length} order{selectedIds.length > 1 ? 's' : ''}{' '}
            selected
          </span>
          <button
            onClick={() => setSelectedIds([])}
            className="text-sm text-text3 hover:text-gray-600 underline"
          >
            Clear selection
          </button>
        </div>
      )}
      {/* Loading State */}
      {loading && (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Loader text="Loading Order Data..." />
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-10">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={refetch}>Try Again</Button>
        </div>
      )}

      {/* Empty State */}
      {/* {!loading && !error && paginatedOrders.length === 0 && (
        <div className="text-center py-10">
          <EmptyScreen 
            title="No Orders Found" 
            description="We couldn't find any orders matching your filters. Try adjusting your search or filters." 
          />
        </div>
        {/* Orders Table */}
      {!loading && !error && (
        <div className="overflow-x-auto">
          <div className="space-y-2">
            {/* Table Header - desktop only */}
            {view === 'list' && (
              <div className="hidden xl:block bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
                <div
                  className={`grid gap-2 xl:gap-4 text-center items-center ${gridColsClass}`}
                >
                  <div className="flex justify-center">
                    <Checkbox
                      checked={
                        selectedIds.length === paginatedOrders.length &&
                        paginatedOrders.length > 0
                      }
                      onChange={toggleSelectAll}
                    />
                  </div>
                  {admin && <span>CRN</span>}
                  <div className="text-center">Order Detail</div>
                  {!isPending && <div>Tracking Detail</div>}{' '}
                  {/* New Column Header */}
                  <div>Pickup & Delivery</div>
                  <div>Package Details</div>
                  <div>Payment</div>
                  <div>Product</div>
                  <div>Zone/Mode</div>
                  {isAllShipment && <div>Status</div>}
                </div>
              </div>
            )}

            {paginatedOrders.length > 0 ? (
              view === 'list' ? (
                <>
                  {/* Desktop List */}
                  <div className="hidden xl:block space-y-2">
                    {paginatedOrders.map((order) => (
                      <div
                        key={order.shipment_id || order.order_id}
                        className={`rounded-xl px-3 xl:px-5 py-1.5 shadow-sm text-center border transition-all duration-200 ${selectedIds.includes(
                          order.shipment_id || order.order_id
                        )
                          ? 'border-2 shadow-sm border-teal bg-teal/10'
                          : 'bg-card border'
                          }`}
                      >
                        <div
                          className={`grid gap-2 xl:gap-4 text-xs xl:text-sm text-gray-700 items-center ${gridColsClass}`}
                        >
                          {/* Checkbox */}
                          <div
                            className="flex items-center justify-center pt-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selectedIds.includes(
                                order.shipment_id || order.order_id
                              )}
                              onChange={() =>
                                toggleRow(order.shipment_id || order.order_id)
                              }
                            />
                          </div>

                          {/* CRN */}
                          {admin && (
                            <div className="text-center font-medium text-text3">
                              {order.crn_number || 'N/A'}
                            </div>
                          )}

                          {/* Order ID */}
                          <div className="space-y-2 group text-center">
                            <ChannelBadge
                              channelId={(order.shop_name?.toLowerCase() || order.channel_id) ?? undefined}
                              orderNumber={
                                (order.order_number?.replace(
                                  /^#/,
                                  ''
                                ) as string) ||
                                (order.order_id?.replace(/^#/, '') as string) ||
                                (order.shipment_id as string)
                              }
                              createdAt={order.created_at}
                              order={order}
                              onSelectOrder={setSelectedOrder}
                            />
                          </div>

                          {/* AWB Number Column - Only shown if not pending */}
                          {!isPending && (
                            <div className="flex flex-col xl:flex-row items-center justify-center group">
                              {order.awb_number ? (
                                <div className="flex flex-col items-center justify-center">
                                  <div className="flex flex-row">
                                    <span
                                      className="text-blue-700 text-xs font-semibold cursor-pointer hover:underline"
                                      onClick={() => handleTrackingClick(order)}
                                    >
                                      {order.awb_number}
                                    </span>
                                    <button
                                      className="h-6 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          order.awb_number || ''
                                        );
                                        toast.success('AWB copied');
                                      }}
                                    >
                                      <CopyIcon className="h-4 w-4 text-gray-400 cursor-pointer" />
                                    </button>
                                  </div>
                                  <p className="text-sm text-text2">
                                    {order.courier_name || 'N/A'}{' '}
                                  </p>
                                </div>
                              ) : (
                                <span className="text-gray-400 italic">
                                  Not Assigned
                                </span>
                              )}
                            </div>
                          )}

                          {/* Pickup & Delivery */}
                          <div className="flex items-center justify-left gap-4 text-sm">
                            <div className="flex flex-col items-center mt-1">
                              <div className="w-2.5 h-2.5 rounded-full bg-teal" />
                              <div className="w-0.5 h-10 border-l-2 border-dotted border-gray-300 my-1" />
                              <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                            </div>
                            <div className="flex flex-col items-start justify-between xl:gap-4 2xl:gap-6 text-left">
                              <div>
                                {order.origin_address?.city ? (
                                  <div className="flex flex-col">
                                    {getPickupDisplayName(order.origin_address).length > 30 ? (
                                      <Tooltip text={getPickupDisplayName(order.origin_address)} side="top">
                                        <p className="text-[12px] font-bold text-text3">
                                          {getPickupDisplayName(order.origin_address).substring(0, 30) + '...'}
                                        </p>
                                      </Tooltip>
                                    ) : (
                                      <p className="text-[12px] font-bold text-text3">
                                        {getPickupDisplayName(order.origin_address)}
                                      </p>
                                    )}
                                    <p className="text-[12px] text-text2 ">
                                      {order.origin_address?.pincode || 'N/A'},
                                      {order.origin_address?.city || 'N/A'},
                                      {order.origin_address?.state || 'N/A'}
                                    </p>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 text-red-500">
                                    <Edit16Filled className="h-3 w-3" />
                                    <p className="text-xs">No Address</p>
                                  </div>
                                )}
                              </div>
                              <div>
                                {order.destination_address?.city ? (
                                  <div className="flex flex-col">
                                    {(order.destination_address?.contact_name || 'Customer').length > 30 ? (
                                      <Tooltip text={order.destination_address?.contact_name || 'Customer'} side="top">
                                        <p className="text-[12px] font-bold text-text3">
                                          {(order.destination_address?.contact_name || 'Customer').substring(0, 30) + '...'}
                                        </p>
                                      </Tooltip>
                                    ) : (
                                      <p className="text-[12px] font-bold text-text3">
                                        {order.destination_address?.contact_name || 'Customer'}
                                      </p>
                                    )}
                                    <p className="text-[12px] text-text2 ">
                                      {order.destination_address?.pincode ||
                                        'N/A'}
                                      ,{' '}
                                      {order.destination_address?.city || 'N/A'}
                                      ,{' '}
                                      {order.destination_address?.state ||
                                        'N/A'}
                                    </p>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 text-red-500">
                                    <Edit16Filled className="h-3 w-3" />
                                    <p className="text-xs">No Address</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Package Detail */}
                          <div className="text-xs space-y-1 text-center font-semibold">
                            <div className="text-gray-700 font-semibold">
                              Pkg. Wt: {order.dead_weight || 0}{' '}
                              {order.weight_unit || 'Kg'}
                            </div>
                            <div className="text-gray-700 font-semibold">
                              Vol. Wt: {order.volumetric_weight || 0}{' '}
                              {order.weight_unit || 'Kg'}
                            </div>
                            <div className="text-gray-700 font-semibold">
                              Dim: ({order.length} x {order.breadth} x{' '}
                              {order.height} {order.dimension_unit || 'cm'})
                            </div>
                            <div className="text-gray-700 font-semibold">
                              Chargeable Wt: {order.chargeable_weight || order.dead_weight || 0}{' '}
                              {order.weight_unit || 'Kg'}
                            </div>
                          </div>

                          {/* Payment */}
                          <div className="text-sm space-y-1">
                            <div className="font-bold text-teal">
                              ₹{order.shipment_value}
                            </div>
                            <div
                              className={`text-sm px-2 py-0.5 lowercase rounded inline-block ${order.payment_mode?.toLowerCase() === 'online'
                                ? 'text-green-600 bg-green-50'
                                : 'text-red-600 bg-red-50 border-red-600'
                                }`}
                            >
                              {order.payment_mode?.toLowerCase() === 'online'
                                ? 'Prepaid'
                                : order.payment_mode}
                            </div>
                          </div>

                          {/* Product */}
                          <div className="relative group text-sm leading-snug text-center max-w-[180px] mx-auto cursor-pointer">
                            <Tooltip text={order.package_desc || 'N/A'}>
                              <div className="truncate">
                                {truncateText(order.package_desc || 'N/A', 20)}
                              </div>
                            </Tooltip>
                            <div className="text-text mt-1 text-center">
                              Qty: {order.quantity || 1}
                            </div>
                          </div>

                          {/* Zone & Mode */}
                          <div className="text-xs xl:text-sm">
                            <div className="font-medium">{order.zone}</div>
                            <div className="text-text3">
                              {order.service_name}
                            </div>
                          </div>

                          {/* Status */}
                          {isAllShipment && (
                            <div className="flex justify-center">
                              <span
                                className={`px-3 py-1 text-xs font-semibold rounded-full border ${getShipmentStatusClasses(order.shipment_status as string)}`}
                              >
                                {formatStatusDisplay(order.shipment_status || 'N/A')}
                              </span>
                            </div>
                          )}
                        </div>

                        <hr className="mt-2 border-dashed" />
                        <div className="flex flex-row items-center justify-between mt-2">
                          <div className="flex gap-2">
                            {order.tags?.split(',').map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2 items-center flex-wrap justify-end">
                            {activeTabCode === 'failed' && (
                              <div className="flex items-center gap-2 mr-2">
                                {order.shipment_message_detail && (
                                  <Tooltip text={order.shipment_message_detail} side="top">
                                    <div className="p-1.5 rounded bg-red-50 hover:bg-red-100 cursor-pointer text-red-500 transition-colors">
                                      <InfoIcon className="w-4 h-4" />
                                    </div>
                                  </Tooltip>
                                )}
                                {admin && (
                                  <Tooltip text="View API Payload" side="top">
                                    <div
                                      className="p-1.5 rounded bg-gray-50 hover:bg-gray-200 cursor-pointer text-gray-700 transition-colors border"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openModal({
                                          Component: () => (
                                            <FailureLogModal
                                              requestPayload={order.shipment_failure_request}
                                              responsePayload={order.shipment_failure_response}
                                              onClose={closeModal}
                                            />
                                          ),
                                          centered: true,
                                          closable: true,
                                          className: 'max-w-4xl p-0 overflow-hidden',
                                        });
                                      }}
                                    >
                                      <Code2Icon className="w-4 h-4" />
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            )}
                            {/* Render Buttons based on Role and Status */}
                            {(() => {
                              const s =
                                order.shipment_status?.toLowerCase() || '';
                              const isAdmin = admin;

                              const tabCode =
                                activeTabCode !== 'all' ? activeTabCode : s;

                              const isReady =
                                tabCode.includes('ready') ||
                                tabCode.includes('pickup') ||
                                tabCode.includes('manifest') ||
                                tabCode.includes('label') ||
                                tabCode.includes('exception');
                              const isInTransit =
                                tabCode.includes('transit') &&
                                !tabCode.includes('rto');
                              const isRTO = tabCode.includes('rto');
                              const isDelivered = tabCode.includes('delivered');
                              const isPending = tabCode === 'pending';
                              const isFailed = tabCode === 'failed';
                              const isCancelled = tabCode === 'cancelled';

                              const isChannelOrder =
                                !!channelFilter ||
                                !!order.channel_id ||
                                !!order.store_id ||
                                (order.shop_name && order.shop_name.toLowerCase() !== 'parcelpicks');

                              const showLabel =
                                !isChannelOrder &&
                                (isReady ||
                                  isPending ||
                                  ((isInTransit || isRTO) && isAdmin));
                              const showManifest =
                                !isChannelOrder &&
                                (isReady || ((isInTransit || isRTO) && isAdmin));
                              const showCancel =
                                isReady ||
                                isPending ||
                                ((isInTransit || isRTO) && isAdmin);
                              const showClone = !isChannelOrder && (isDelivered || isPending || isFailed || isCancelled);
                              const isMissingFields = isChannelOrder && (
                                !order.destination_address?.address1 ||
                                !order.destination_address?.city ||
                                !order.destination_address?.state ||
                                !order.destination_address?.pincode ||
                                !order.destination_address?.contact_no ||
                                !order.destination_address?.contact_name ||
                                !order.dead_weight || Number(order.dead_weight) <= 0 ||
                                !order.length || Number(order.length) <= 0 ||
                                !order.breadth || Number(order.breadth) <= 0 ||
                                !order.height || Number(order.height) <= 0
                              );

                              const showEdit = isChannelOrder && isMissingFields && isPending;
                              const showShipNow = isPending && (!isChannelOrder || !isMissingFields);

                              return (
                                <>
                                  {showLabel && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      disabled={isGeneratingLabel}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleIndividualLabel(order);
                                      }}
                                    >
                                      {isGeneratingLabel
                                        ? 'Generating...'
                                        : 'Get Label'}
                                    </Button>
                                  )}
                                  {showManifest && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleBulkManifest([order]);
                                      }}
                                    >
                                      Manifest
                                    </Button>
                                  )}
                                  {showCancel && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="border-red-500 text-red-500 hover:bg-red-100 hover:text-red-500"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const shipmentId = order.shipment_id;
                                        if (shipmentId) {
                                          const triggerCancel = () => {
                                            closeModal();
                                            const promise = handleCancelOrder(shipmentId);
                                            toast.promise(promise, {
                                              loading: 'Cancelling order...',
                                              success: 'Order cancelled successfully',
                                              error: (err: any) =>
                                                typeof err === 'string'
                                                  ? err
                                                  : err?.message || 'Failed to cancel order',
                                            });
                                          };
                                          openModal({
                                            element: (
                                              <CancellationModalContent
                                                orderNumber={order.order_number || ''}
                                                onCancelOrder={triggerCancel}
                                                onCancelShipment={triggerCancel}
                                                onClose={closeModal}
                                              />
                                            ),
                                            centered: true,
                                            closable: true,
                                            className: 'max-w-lg',
                                          });
                                        } else {
                                          toast.error(
                                            'No shipment ID exists for this order'
                                          );
                                        }
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  )}
                                  {showClone && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCloneOrder(order);
                                      }}
                                    >
                                      Clone Order
                                    </Button>
                                  )}
                                  {showEdit && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openModal({
                                          Component: () => (
                                            <EditChannelOrderModal
                                              order={order}
                                              onClose={closeModal}
                                              onSuccess={(updatedOrder) => {
                                                closeModal();
                                                openModal({
                                                  Component: () => (
                                                    <ConfirmationModal
                                                      order={updatedOrder}
                                                      onClose={() => {
                                                        closeModal();
                                                        refetch();
                                                      }}
                                                    />
                                                  ),
                                                  centered: true,
                                                  closable: true,
                                                  className: 'max-w-lg',
                                                });
                                              }}
                                            />
                                          ),
                                          centered: true,
                                          closable: true,
                                          className: 'max-w-4xl',
                                        });
                                      }}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                  {showShipNow && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleShipNow(order);
                                      }}
                                    >
                                      Ship Now
                                    </Button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tablet/Large Mobile Grid (below xl) */}
                  <div className="xl:hidden grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {paginatedOrders.map((order) => (
                      <OrderCard
                        key={order.shipment_id || order.order_id}
                        order={order}
                        selectable={true}
                        isSelected={selectedIds.includes(
                          order.shipment_id || order.order_id
                        )}
                        onSelectChange={() =>
                          toggleRow(order.shipment_id || order.order_id)
                        }
                        onSelect={setSelectedOrder}
                      />
                    ))}
                  </div>
                </>
              ) : (
                /* Desktop Grid View */
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
                  {paginatedOrders.map((order) => (
                    <OrderCard
                      key={order.shipment_id || order.order_id}
                      order={order}
                      selectable={true}
                      isSelected={selectedIds.includes(
                        order.shipment_id || order.order_id
                      )}
                      onSelectChange={() =>
                        toggleRow(order.shipment_id || order.order_id)
                      }
                      onSelect={setSelectedOrder}
                    />
                  ))}
                </div>
              )
            ) : (
              /* Empty State - Rendered below header */
              <div className="flex flex-col items-center justify-center p-12 text-center bg-white rounded-xl border border-gray-100 shadow-sm min-h-[400px]">
                <PackageSearch className="w-16 h-16 text-gray-200 mb-4" />
                <h2 className="text-xl font-bold text-text mb-2">
                  No Orders Found
                </h2>
                <p className="text-text2 max-w-sm mx-auto mb-8">
                  {searchTerm
                    ? `No orders matching "${searchTerm}" were found. Try a different search term.`
                    : "You haven't placed any orders yet. Start shipping today!"}
                </p>
                {!searchTerm && (
                  <Button
                    className="bg-teal text-white hover:bg-teal-dark px-8 rounded-full"
                    onClick={() => navigate('/orders/add')}
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Place New Order
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && !paginatedOrders.length && (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border">
          <Loader text="Loading orders..." />
        </div>
      )}

      {!loading && !error && (apiOrders?.length > 0 || totalCount > 0) && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Showing page {currentPage}
            {totalCount > rowsPerPage && totalPages > 0
              ? ` of ${totalPages}`
              : ''}
          </div>
          <div className="flex items-center gap-3">
            <Select
              options={pageSizeOptions}
              value={String(rowsPerPage)}
              position="top"
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            />
            <Button
              variant="outline"
              onClick={() => {
                setCurrentPage((prev) => Math.max(prev - 1, 1));
                topRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setCurrentPage((prev) =>
                  totalCount > rowsPerPage && totalPages > 0
                    ? Math.min(prev + 1, totalPages)
                    : prev + 1
                );
                topRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              disabled={
                totalCount > rowsPerPage && totalPages > 0
                  ? currentPage >= totalPages
                  : !apiOrders || apiOrders.length < rowsPerPage
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Drawer />
      <CustomModal />
    </div>
  );
};

export default AllOrdersPage;
